import {Button} from "@/components/button/Button.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {cappedUptimePercentage} from "@/lib/format.tsx";
import {formatResponseTime} from "@/pages/checks/analysis/overview/utils.ts";
import {formatDurationRounded} from "@/pages/statuspages/consumption/utils/uptimeBarEvents.ts";
import classNames from "classnames";
import {FiChevronDown, FiChevronRight} from "react-icons/fi";
import {Link} from "react-router";
import styles from "./ComponentHistoryTable.module.scss";
import type {ComponentHistoryTableEntry} from "./ComponentHistoryTable.types.ts";

interface TableRowProps {
  entry: ComponentHistoryTableEntry;
  isExpanded: boolean;
  onToggle: () => void;
  isChild?: boolean;
  hasDivider?: boolean;
  calculationType: "BY_INCIDENTS" | "BY_CHECKS";
  allowDrillDown: boolean;
  componentLinkTo?: (serviceId: number) => string;
}

// Response-time metrics render a duration (formatResponseTime mirrors legacy autoDuration: seconds
// input, sub-second → ms). SSL/cert (DAYS_TO_EXPIRY) instead carries days-to-expiry in this field.
// Every other type (NTP jitter, NONE, …) shows "n/a", matching legacy's _shouldDisplayResponseAvg.
const RESPONSE_TIME_TYPES = new Set(["RESPONSE_TIME", "CUSTOM", "RUM_LOAD_TIME"]);

const formatComponentMetric = (value: number | null, performanceDataType?: string | null): string => {
  if (value === null || value === undefined) return "n/a";

  if (performanceDataType === "DAYS_TO_EXPIRY") {
    // Certificates/SSL: value is days-to-expiry, not a duration.
    if (value <= 0) return "";
    return `Expires: ${Math.round(value)} days`;
  }

  if (!RESPONSE_TIME_TYPES.has(performanceDataType ?? "")) return "n/a";
  return formatResponseTime(value, "n/a");
};

const formatUptimePercentage = (pct?: number, sla?: number | null): {text: string; className: string} => {
  const text = cappedUptimePercentage(pct);

  let className = "text-success";
  if (sla !== null && sla !== undefined) {
    if ((pct ?? 0) * 100 < sla) {
      className = "text-danger";
    } else if ((pct ?? 0) * 100 < sla + 1) {
      className = "text-warning";
    }
  }

  return {text, className};
};

export const TableRow = ({
  entry,
  isExpanded,
  onToggle,
  isChild = false,
  hasDivider = true,
  calculationType,
  allowDrillDown,
  componentLinkTo,
}: TableRowProps) => {
  const renderName = () => {
    const nameContent = entry.name;
    // Keyed by check id, matching the legacy component page URL — see ComponentItem.
    const serviceId = entry.serviceId;
    const drillDownLink =
      allowDrillDown && !entry.isGroup && entry.isDrillDown && serviceId != null && componentLinkTo
        ? componentLinkTo(serviceId)
        : null;

    return (
      <>
        {entry.isGroup ? (
          <Button
            variant="ghost"
            className={styles.expandButton}
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse group" : "Expand group"}
          >
            <Icon icon={isExpanded ? FiChevronDown : FiChevronRight} size="normal" />
          </Button>
        ) : (
          <span className={styles.expandButtonPlaceholder} />
        )}
        {drillDownLink ? (
          <Button
            as={Link}
            to={drillDownLink}
            variant="linkBlue"
            size="text"
            className={classNames(styles.name, {
              [styles.nameLink]: true,
              [styles.nameChild]: isChild,
            })}
          >
            {nameContent}
          </Button>
        ) : entry.isGroup ? (
          <Button variant="ghost" className={classNames(styles.name, styles.nameGroup)} onClick={onToggle}>
            {nameContent}
          </Button>
        ) : (
          <span
            className={classNames(styles.name, {
              [styles.nameChild]: isChild,
            })}
          >
            {nameContent}
          </span>
        )}
      </>
    );
  };

  if (calculationType === "BY_CHECKS") {
    const uptimeInfo = formatUptimePercentage(entry.uptimePct, entry.serviceUptimeSla);

    return (
      <div className={classNames(styles.tableRow, {[styles.tableRowDivider]: hasDivider})}>
        <div className={styles.nameCell}>{renderName()}</div>
        <div className={classNames(styles.statCell, styles.uptimeCell)}>
          <span className={classNames(styles.statValue, uptimeInfo.className, "font-weight-bold")}>
            {uptimeInfo.text}
          </span>
        </div>
        <div className={classNames(styles.statCell, styles.outagesCell)}>
          <span className={styles.statValue}>{entry.outages !== undefined ? entry.outages : "n/a"}</span>
        </div>
        <div className={classNames(styles.statCell, styles.downtimeCell)}>
          <span className={styles.statValue}>
            {entry.downtimeSecs !== undefined ? formatDurationRounded(entry.downtimeSecs) : "n/a"}
          </span>
        </div>
        <div className={classNames(styles.statCell, styles.responseTimeCell)}>
          {!entry.isGroup && (
            <span className={styles.statValue}>
              {formatComponentMetric(entry.avgResponseTime, entry.performanceDataType)}
            </span>
          )}
        </div>
      </div>
    );
  }

  // BY_INCIDENTS
  return (
    <div className={classNames(styles.tableRow, {[styles.tableRowDivider]: hasDivider})}>
      <div className={styles.nameCell}>{renderName()}</div>
      <div className={classNames(styles.statCell, styles.majorOutageCell)}>
        <span className={styles.statValue}>{entry.majorOutage}</span>
      </div>
      <div className={classNames(styles.statCell, styles.partialOutageCell)}>
        <span className={styles.statValue}>{entry.partialOutage}</span>
      </div>
      <div className={classNames(styles.statCell, styles.degradedCell)}>
        <span className={styles.statValue}>{entry.degradedPerformance}</span>
      </div>
      <div className={classNames(styles.statCell, styles.responseTimeCell)}>
        {!entry.isGroup && (
          <span className={styles.statValue}>
            {formatComponentMetric(entry.avgResponseTime, entry.performanceDataType)}
          </span>
        )}
      </div>
    </div>
  );
};
